import Echo from 'laravel-echo'
/**
 * laravel-echo-server (образ oanhnn) использует Socket.IO v2 на сервере.
 * socket.io-client v3/v4 несовместимы по протоколу → connect_error "server error".
 * @see https://github.com/tlaverdure/laravel-echo-server/issues/576
 */
import * as SocketIO from 'socket.io-client'

function resolveSocketIo(): SocketIOClientStatic {
  const m = SocketIO as unknown as Record<string, unknown>
  for (const key of ['default', 'io'] as const) {
    const v = m[key]
    if (typeof v === 'function') return v as SocketIOClientStatic
  }
  if (typeof SocketIO === 'function') return SocketIO as unknown as SocketIOClientStatic
  throw new Error('[Echo] socket.io-client: не найдена функция io (проверьте зависимость socket.io-client@2.x)')
}

const io = resolveSocketIo()

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4545/api'

const WS_HOST = import.meta.env.VITE_ECHO_WS_HOST || 'localhost'
const WS_PORT = Number(import.meta.env.VITE_ECHO_WS_PORT || 6001)
const FORCE_TLS = String(import.meta.env.VITE_ECHO_FORCE_TLS || 'false') === 'true'

/**
 * В dev Vite поднимает SPA на :5173, echo — на :6002 (другой origin).
 * XHR к echo без CORS → пустой body → engine.io «parser error».
 * Прокси `/socket.io` в vite.config → подключаемся к `window.location.origin` (тот же origin, что и у страницы).
 */
const ECHO_WS_DEV_PROXY = String(import.meta.env.VITE_ECHO_WS_DEV_PROXY ?? 'true') === 'true'

/** Полный URL для Engine.IO (host без схемы в SPA даёт неочевидный разбор в части окружений). */
function getSocketOrigin(): string {
  // Внешний if (DEV) нужен, чтобы в prod-бандле terser вырезал весь dev-proxy блок.
  if (import.meta.env.DEV) {
    if (ECHO_WS_DEV_PROXY && typeof window !== 'undefined') {
      return window.location.origin
    }
  }

  const h = String(WS_HOST).trim()
  if (/^https?:\/\//i.test(h)) {
    return h.replace(/\/$/, '')
  }
  const hostWithPort = h.includes(':') ? h : `${h}:${WS_PORT}`
  const scheme = FORCE_TLS ? 'https' : 'http'
  return `${scheme}://${hostWithPort}`
}

const ECHO_DEBUG = String(import.meta.env.VITE_ECHO_DEBUG || 'false') === 'true'

const getAuthToken = () => localStorage.getItem('auth_token')

/** Laravel: маршруты broadcasting в routes/api.php → /api/broadcasting/auth (JWT, без CSRF). */
const getAuthEndpoint = () => {
  const base = API_URL.replace(/\/api\/?$/, '')
  return `${base}/api/broadcasting/auth`
}

let echoInstance: Echo<'socket.io'> | null = null

function echoLog(...args: unknown[]) {
  if (ECHO_DEBUG || import.meta.env.DEV) {
    console.info('[Echo]', ...args)
  }
}

export function getEchoClient() {
  if (echoInstance) return echoInstance

  const token = getAuthToken()

  // laravel-echo для broadcaster="socket.io" требует socket.io-client (либо глобально window.io, либо через options.client).
  ;(window as unknown as { io?: typeof io }).io = io

  const socketOrigin = getSocketOrigin()
  echoLog('Socket.IO origin:', socketOrigin)

  // laravel-echo-server в проекте поднят с Socket.IO эндпоинтом (/socket.io),
  // поэтому подключаемся как broadcaster = "socket.io".
  echoInstance = new Echo({
    broadcaster: 'socket.io',
    client: io,
    host: socketOrigin,
    secure: FORCE_TLS,
    disableStats: true,
    authEndpoint: getAuthEndpoint(),
    /** Токен читается при первом создании Echo; после логина вызовите resetEchoClient() чтобы подхватить JWT. */
    auth: {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  })

  const socket = getEchoSocketUnsafe(echoInstance)
  if (socket) {
    socket.on('connect', () => echoLog('Socket.IO connect', socket.id))
    socket.on('disconnect', (reason: string) => echoLog('Socket.IO disconnect', reason))
    socket.on(
      'connect_error',
      (err: Error & { description?: string; data?: unknown; code?: string | number }) =>
        echoLog(
          'Socket.IO connect_error',
          err?.message ?? err,
          'code:',
          err?.code,
          'description:',
          err?.description,
          'data:',
          err?.data,
        ),
    )
  }

  return echoInstance!
}

/** Низкоуровневый socket.io (для отладки в консоли). */
function getEchoSocketUnsafe(echo: Echo<'socket.io'>): SocketIOClient.Socket | null {
  const connector = echo.connector as unknown as { socket?: SocketIOClient.Socket }
  return connector.socket ?? null
}

export function getEchoSocket(): SocketIOClient.Socket | null {
  if (!echoInstance) return null
  return getEchoSocketUnsafe(echoInstance)
}

/** Сбросить Echo после смены токена (иначе private-каналы авторизуются со старым Bearer). */
export function resetEchoClient() {
  if (!echoInstance) return
  try {
    echoInstance.disconnect()
  } catch {
    /* ignore */
  }
  echoInstance = null
}

export function disconnectEchoClient() {
  resetEchoClient()
}

